import React, { useState, useEffect } from 'react';
import {
  FiFolder, FiFileText, FiUsers, FiBell, FiArrowRight,
  FiAlertCircle, FiClock, FiZap, FiBookOpen, FiRefreshCw,
  FiEdit, FiChevronRight, FiPlus,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Dashboard.css';

// ── helpers ───────────────────────────────────────────────────────────────────

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function formatNextRun(isoString) {
  if (!isoString) return 'Not scheduled';
  const d = new Date(isoString);
  const diff = d - Date.now();
  if (diff < 0) return 'Running soon';
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

function formatDueDate(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  const diff = d - Date.now();
  const days = Math.floor(diff / 86400000);
  if (days < 0) return { label: 'Overdue', overdue: true };
  if (days === 0) return { label: 'Due today', overdue: false };
  if (days === 1) return { label: 'Due tomorrow', overdue: false };
  return { label: `Due in ${days}d`, overdue: false };
}

const PRIORITY_CLASS = { high: 'priority-high', medium: 'priority-med', low: 'priority-low' };

const STAGE_LABELS = {
  // Grant proposal
  nofo_input:       'NOFO Input',
  funder_research:  'Funder Research',
  pi_profile:       'PI Profile',
  team_building:    'Team Building',
  drafting:         'Drafting',
  compliance:       'Compliance Review',
  // Paper draft
  collection_input: 'Setup',
  outline:          'Outline',
  author_profile:   'Author Profile',
  // shared
  done:             'Complete',
};

const GRANT_STAGE_ORDER  = ['nofo_input','funder_research','pi_profile','team_building','drafting','compliance','done'];
const PAPER_STAGE_ORDER  = ['collection_input','outline','author_profile','drafting','done'];

function StageProgress({ currentStage, stageOrder = GRANT_STAGE_ORDER }) {
  const idx = stageOrder.indexOf(currentStage);
  const pct = Math.round((Math.max(0, idx) / (stageOrder.length - 1)) * 100);
  return (
    <div className="db-stage-progress">
      <div className="db-stage-bar">
        <div className="db-stage-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="db-stage-label">{STAGE_LABELS[currentStage] || currentStage}</span>
    </div>
  );
}

// ── component ─────────────────────────────────────────────────────────────────

const Dashboard = ({ user, onNavigate, onOpenProposal, onOpenDraft }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiEndpoint('/api/dashboard'), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load dashboard');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  if (loading) {
    return (
      <div className="db-loading">
        <div className="spinner" />
        <p>Loading your dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="db-error">
        <FiAlertCircle /> {error}
        <button onClick={fetchDashboard}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  const { stats, collections, teams, monitors, action_items,
          active_proposals = [], active_drafts = [] } = data;
  const recentCollections = collections.slice(0, 4);
  const activeMonitors = monitors.filter(m => m.is_active).slice(0, 4);
  const openItems = action_items.slice(0, 5);
  const hasActiveWork = active_proposals.length > 0 || active_drafts.length > 0;

  return (
    <div className="dashboard">

      {/* ── Start something new ──────────────────────────────────────────── */}
      <div className="db-start-row">
        <button className="db-start-btn db-start-btn--grant" onClick={() => onNavigate('grant-agent')}>
          <FiEdit className="db-start-icon" />
          <div>
            <strong>Write a Grant Proposal</strong>
            <span>From NOFO to full draft with AI assistance</span>
          </div>
          <FiChevronRight className="db-start-arrow" />
        </button>
        <button className="db-start-btn db-start-btn--paper" onClick={() => onNavigate('paper-agent')}>
          <FiFileText className="db-start-icon" />
          <div>
            <strong>Write a Paper</strong>
            <span>Structure, draft, and refine a research paper</span>
          </div>
          <FiChevronRight className="db-start-arrow" />
        </button>
        <button className="db-start-btn db-start-btn--search" onClick={() => onNavigate('search')}>
          <FiBookOpen className="db-start-icon" />
          <div>
            <strong>Explore Literature</strong>
            <span>Search and build a paper collection</span>
          </div>
          <FiChevronRight className="db-start-arrow" />
        </button>
      </div>

      {/* ── Active work ──────────────────────────────────────────────────── */}
      {hasActiveWork && (
        <div className="db-active-work">
          <h3 className="db-section-title">Continue where you left off</h3>
          <div className="db-work-grid">
            {active_proposals.map(p => (
              <div
                key={`proposal-${p.id}`}
                className="db-work-card db-work-card--proposal"
                onClick={() => onOpenProposal && onOpenProposal(p.id)}
              >
                <div className="db-work-card-type">Grant Proposal</div>
                <div className="db-work-card-title">{p.title}</div>
                {p.nofo_number && <div className="db-work-card-nofo">{p.nofo_number}</div>}
                <StageProgress currentStage={p.current_stage} />
                <div className="db-work-card-footer">
                  <span className="db-muted">{timeAgo(p.updated_at)}</span>
                  <span className="db-work-continue">Continue <FiArrowRight /></span>
                </div>
              </div>
            ))}
            {active_drafts.map(d => (
              <div
                key={`draft-${d.id}`}
                className="db-work-card db-work-card--draft"
                onClick={() => onOpenDraft && onOpenDraft(d.id)}
              >
                <div className="db-work-card-type">Paper Draft</div>
                <div className="db-work-card-title">{d.title}</div>
                <StageProgress currentStage={d.current_stage} stageOrder={PAPER_STAGE_ORDER} />
                <div className="db-work-card-footer">
                  <span className="db-muted">{timeAgo(d.updated_at)}</span>
                  <span className="db-work-continue">Continue <FiArrowRight /></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Secondary grid ───────────────────────────────────────────────── */}
      <div className="db-grid">

        {/* Recent collections */}
        <div className="db-card">
          <div className="db-card-header">
            <h3><FiFolder /> Collections</h3>
            <button className="db-card-link" onClick={() => onNavigate('collections')}>
              View all <FiArrowRight />
            </button>
          </div>
          {recentCollections.length === 0 ? (
            <div className="db-empty">
              <button className="db-empty-cta" onClick={() => onNavigate('collections')}>
                <FiPlus /> Create your first collection
              </button>
            </div>
          ) : (
            <ul className="db-collection-list">
              {recentCollections.map(c => (
                <li key={c.id} className="db-collection-item" onClick={() => onNavigate('collections')}>
                  <div className="db-collection-main">
                    <span className="db-collection-name">{c.name}</span>
                    {c.is_shared && <span className="db-shared-badge">Shared</span>}
                  </div>
                  <div className="db-collection-meta">
                    <span>{c.item_count} papers</span>
                    <span className="db-muted">{timeAgo(c.updated_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Open action items */}
        {openItems.length > 0 && (
          <div className="db-card">
            <div className="db-card-header">
              <h3><FiZap /> Action Items</h3>
              <button className="db-card-link" onClick={() => onNavigate('collections')}>
                View all <FiArrowRight />
              </button>
            </div>
            <ul className="db-action-list">
              {openItems.map(item => {
                const due = formatDueDate(item.due_date);
                return (
                  <li key={item.id} className="db-action-item" onClick={() => onNavigate('collections')}>
                    <span className={`db-priority-dot ${PRIORITY_CLASS[item.priority]}`} />
                    <div className="db-action-body">
                      <span className="db-action-title">{item.title}</span>
                      <div className="db-action-meta">
                        {item.assignee_username && <span className="db-muted">→ {item.assignee_username}</span>}
                        {due && <span className={due.overdue ? 'db-overdue' : 'db-muted'}>{due.label}</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Active monitors */}
        {activeMonitors.length > 0 && (
          <div className="db-card">
            <div className="db-card-header">
              <h3><FiBell /> Monitors</h3>
              <button className="db-card-link" onClick={() => onNavigate('monitors')}>
                View all <FiArrowRight />
              </button>
            </div>
            <ul className="db-monitor-list">
              {activeMonitors.map(m => (
                <li key={m.id} className="db-monitor-item" onClick={() => onNavigate('monitors')}>
                  <div className="db-monitor-name">{m.name}</div>
                  <div className="db-monitor-meta">
                    <span className="db-cadence-badge">{m.cadence}</span>
                    <span className="db-muted">
                      <FiClock style={{ verticalAlign: 'middle', marginRight: 3 }} />
                      {formatNextRun(m.next_run_at)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Teams */}
        {teams.length > 0 && (
          <div className="db-card">
            <div className="db-card-header">
              <h3><FiUsers /> Teams</h3>
              <button className="db-card-link" onClick={() => onNavigate('teams')}>
                View all <FiArrowRight />
              </button>
            </div>
            <ul className="db-team-list">
              {teams.slice(0, 4).map(t => (
                <li key={t.id} className="db-team-item" onClick={() => onNavigate('teams')}>
                  <div className="db-team-main">
                    <span className="db-team-name">{t.name}</span>
                    {t.is_owner && <span className="db-owner-badge">Owner</span>}
                  </div>
                  <span className="db-muted">{t.member_count} member{t.member_count !== 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
